import { config } from '@/src/config/app-config';
import { MILLIS_PER_HOUR } from '@/src/constants/DateTimeConstants';
import { useAudio } from '@/src/hooks/store/useAudio';
import { useJamLeaderboard } from '@/src/store/jam-leaderboard.context';
import { JamEventStatus } from '@/src/types/JamCommon';
import { isLocal } from '@/src/utils/env.utils';
import { i18nKeys } from '@/src/utils/i18n.utils';
import { FlashbarProps } from '@amzn/awsui-components-react';
import { mqtt5 } from 'aws-iot-device-sdk-v2/dist/browser';
import { noop, unescape } from 'lodash';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChatMessage } from '../types/Chat';
import { EnableMessagingResponse } from '../types/EnableMessagingResponse';
import { JamEvent } from '../types/JamEvent';
import { NotificationData, OfflineNotificationMessage } from '../types/JamNotifications';
import { JamSupportChat } from '../types/JamSupportChat';
import { JamSupportChatMessage } from '../types/JamSupportChatMessage';
import { WebSocketsMessage, WsMessageType } from '../types/WebsocketMessages';
import { localLogger } from '../utils/log.utils';
import { MQTTService } from '../utils/mqtt.utils';
import { useApi } from './api.context';
import { useCampaigns } from './campaigns.context';
import { useFlashbars } from './flashbar.context';
import { useJamChallengeDetails } from './jam-challenge-details.context';
import { useJamChallenge } from './jam-challenge.context';
import { useJamChat } from './jam-chat.context';
import { useJamEventDetails } from './jam-event-details.context';
import { useJamMyTeam } from './jam-myteam.context';
import { useUser } from './user.context';

export interface WebsocketContextValue {
  isLoaded: boolean;
  destroyConnection: () => void;
}

const WebsocketContext = React.createContext<WebsocketContextValue>({
  isLoaded: false,
  destroyConnection: noop,
});

const MAX_CONNNECTION_FAILURE_RETRIES = 5;

const WebsocketProvider: React.FC = ({ children }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const { jamMessageApi } = useApi();
  const {
    eventName,
    updateParticipantEventMessages,
    updateEventByJamEvent,
    updateEventIncidents,
    loadEventDetails,
    event,
  } = useJamEventDetails();
  const { loadLeaderboard, isPresenterMode } = useJamLeaderboard();
  const { updateTeamChatMessages, updateSupportChatMessages, updateSupportChatMessage, loadSupportChats } =
    useJamChat();
  const { loadJamChallengeData } = useJamChallenge();
  const { handleWSChallengeProgressUpdate, handleWSLabAssignmentUpdate } = useJamChallengeDetails();
  const { handleTeamRenamedWSMessage } = useJamMyTeam();
  const { t } = useTranslation();
  const mqttClient = useRef<MQTTService>();
  const credentials = useRef<EnableMessagingResponse | undefined>();
  const credentialsRetrieved = useRef<number>(0);
  const connectionErrorsCount = useRef<number>(0);
  const connectionErrorFlashbarId = useRef<string>('');
  const { user } = useUser();
  const { addFlashbar } = useFlashbars();
  const { setAudio } = useAudio();
  const {
    // updateCampaignAttemptFromWSMessage,
    getCampaignEventDetails,
  } = useCampaigns();
  const userExist = !!user?.email;
  const isCampaign = event?.type === 'CAMPAIGN_GROUP';
  const eventHasEnded = useMemo(
    () =>
      event?.eventStatus === JamEventStatus.ENDED ||
      (!!event?.eventEndDate && new Date(event.eventEndDate).getTime() < Date.now()),
    [event?.eventStatus, event?.eventEndDate, eventName]
  );

  const handleAnnouncementMessage = (announcementId: string) => {
    if (!config.ff.enableVirtualMc) {
      return;
    }

    const domain = isLocal() ? 'https://beta.non-prod.jam.training.aws.dev' : '';
    const url = `${domain}/announcements/${announcementId}.mp3`;

    setAudio(new Audio(url));
  };

  const getCredentials = useMemo(
    () => async () => {
      if (
        eventName &&
        (!credentials.current ||
          !(credentialsRetrieved.current && credentialsRetrieved.current + MILLIS_PER_HOUR > Date.now()))
      ) {
        const response = await jamMessageApi.enableMessaging({ eventName, silent: true });
        credentials.current = response;
        credentialsRetrieved.current = Date.now();
        return response;
      }
      return credentials.current;
    },
    [eventName]
  );

  const initialize = async () => {
    try {
      const creds = await getCredentials();

      if (creds) {
        mqttClient.current = new MQTTService(creds);
        mqttClient.current.listen({ onReceiveMessage, onCredentialsUpdated, onConnectionFailure, onConnectionSuccess });
      }
    } catch (e) {
      localLogger('error initializing mqtt client', e);
    }
    setIsLoaded(true);
  };

  const onConnectionSuccess = () => {
    connectionErrorsCount.current = 0;
    connectionErrorFlashbarId.current = '';
    if (eventName) {
      void loadSupportChats(eventName);
    }
  };

  const onReceiveMessage: mqtt5.MessageReceivedEventListener = (receivedEvent) => {
    const message = JSON.parse(receivedEvent.message.payload as string) as WebSocketsMessage<any>;
    localLogger('received WS message', receivedEvent.message.topicName, message);

    switch (message.messageType) {
      case WsMessageType.TeamChatWSMessage: {
        const chatMessages = message.messageBody.map(ChatMessage.fromPlainObject) as ChatMessage[];
        updateTeamChatMessages(chatMessages);
        break;
      }
      case WsMessageType.SupportChatWSMessage: {
        const supportChat = JamSupportChat.fromPlainObject(message.messageBody);
        if (supportChat) {
          updateSupportChatMessages(supportChat);
        }
        break;
      }
      case WsMessageType.SupportChatUpdateWSMessage: {
        updateSupportChatMessage(JamSupportChatMessage.fromPlainObject(message.messageBody));
        break;
      }
      case WsMessageType.EventWSMessage: {
        const wsEvent = JamEvent.fromPlainObject(message.messageBody);
        updateEventByJamEvent(wsEvent);
        void loadJamChallengeData({ id: eventName, showLoader: false });
        break;
      }
      case WsMessageType.TeamChallengeAnswerWSMessage: {
        void handleWSChallengeProgressUpdate(message?.messageBody?.teamChallengeAnswer || message?.messageBody);
        void loadJamChallengeData({ id: eventName, showLoader: false });
        break;
      }
      // LabAvailabilityWSMessage is intentionally unhandled: pool availability doesn't change a team's state.
      case WsMessageType.LabAssignmentWSMessage:
        handleWSLabAssignmentUpdate(message.messageBody ?? {});
        break;
      case WsMessageType.NotificationWSMessage:
        onNotificationMessage(message.messageBody);
        break;
      case WsMessageType.ParticipantMessagesWSMessage: {
        const messages: OfflineNotificationMessage[] = (message.messageBody || []).map(
          OfflineNotificationMessage.fromPlainObject
        ) as OfflineNotificationMessage[];
        updateParticipantEventMessages(messages);
        break;
      }
      case WsMessageType.EventIncidentWSMessage: {
        updateEventIncidents(message.messageBody);
        break;
      }
      case WsMessageType.EventLogWSMessage: {
        handleTeamRenamedWSMessage(message.messageBody);
        break;
      }
      case WsMessageType.TeamParticipantModelWSMessage: {
        if (isCampaign) {
          void getCampaignEventDetails(eventName, false, true);
        } else {
          void loadEventDetails(eventName);
        }
        break;
      }
      case WsMessageType.EventAnnouncementWSMessage: {
        handleAnnouncementMessage(message.messageBody.announcementId);
        break;
      }
      case WsMessageType.LeaderboardWSMessage: {
        if (isPresenterMode) {
          loadLeaderboard(1, 15);
        }
        break;
      }
    }
  };

  const onCredentialsUpdated = async (): Promise<EnableMessagingResponse | undefined> => {
    if (!eventHasEnded && mqttClient.current) {
      return await getCredentials();
    }
  };

  const onConnectionFailure = async (): Promise<EnableMessagingResponse | undefined> => {
    connectionErrorsCount.current = connectionErrorsCount.current + 1;
    if (connectionErrorsCount.current > MAX_CONNNECTION_FAILURE_RETRIES) {
      if (!connectionErrorFlashbarId.current) {
        const id = `${new Date().getTime()}`;
        addFlashbar({
          content: t(i18nKeys.errors.pleaseRefresh),
          header: t(i18nKeys.errors.network.connectionAborted),
          type: 'warning',
          dismissible: true,
          i18nKeys: [],
          onDismiss: () => {
            connectionErrorFlashbarId.current = '';
          },
          id,
        });
        connectionErrorFlashbarId.current = id;
      }
      return;
    }
    if (!eventHasEnded && mqttClient.current) {
      return await getCredentials();
    }
  };

  const onNotificationMessage = (notificationData: NotificationData) => {
    const {
      notificationMessage: { message, title, type },
      options,
    } = notificationData;
    if (!(['success', 'warning', 'info', 'error'] as FlashbarProps.Type[]).includes(type as FlashbarProps.Type)) {
      return;
    }
    addFlashbar({
      content: unescape(decodeURIComponent(message)),
      header: unescape(decodeURIComponent(title)),
      type: type as FlashbarProps.Type,
      dismissible: true,
      i18nKeys: [],
      id: `${new Date().getTime()}`,
      autoDismiss: !!options.timeOut,
      dismissDuration: options.timeOut,
    });
  };

  const destroyConnection = () => {
    localLogger('connection destroying');
    void mqttClient.current?.destroy();
  };

  // initialize mqtt client to listen to websocket
  useEffect(() => {
    if (!eventName) {
      return;
    }
    if (eventHasEnded) {
      destroyConnection();
    } else {
      void initialize();
    }
    return () => destroyConnection();
  }, [eventName, eventHasEnded]);

  useEffect(() => {
    if (!userExist) {
      void mqttClient.current?.destroy();
    }
  }, [userExist]);

  const data: WebsocketContextValue = { isLoaded, destroyConnection };

  return <WebsocketContext.Provider value={data}>{children}</WebsocketContext.Provider>;
};

const useWebsocket = () => {
  const context = useContext(WebsocketContext);
  if (context === undefined) {
    throw new Error('useWebsocket can only be used inside WebsocketProvider');
  }
  return context;
};

export { useWebsocket, WebsocketProvider };
